import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Flex,
  Box,
  Badge,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Heading
} from '@chakra-ui/react';

const SalesBreakdownModal = ({ 
  isOpen, 
  onClose, 
  salesBreakdownData, 
  salesBreakdownLoading, 
  totalSales 
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <Flex align="center" gap={3}>
            <Text>💰 Sales Breakdown</Text>
            <Badge colorScheme="green" fontSize="md">
              ${totalSales?.toFixed(2) || '0.00'} Total
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          {salesBreakdownLoading ? (
            <Flex justify="center" align="center" height="200px">
              <Spinner size="xl" color="blue.500" />
              <Text ml={4}>Loading sales breakdown...</Text>
            </Flex>
          ) : salesBreakdownData.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              <Text>No sales data found for this event.</Text>
            </Alert>
          ) : (
            <VStack spacing={4} align="stretch">
              {/* Summary Stats */}
              <Flex gap={4} flexWrap="wrap">
                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                  <Text fontSize="sm" color="gray.600">Total Orders</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                    {salesBreakdownData.length}
                  </Text>
                </Box>
                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                  <Text fontSize="sm" color="gray.600">Total Revenue</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.500">
                    ${salesBreakdownData.reduce((sum, order) => sum + order.orderTotal, 0).toFixed(2)}
                  </Text>
                </Box>
                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                  <Text fontSize="sm" color="gray.600">Avg Order Value</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="purple.500">
                    ${salesBreakdownData.length > 0 ? (salesBreakdownData.reduce((sum, order) => sum + order.orderTotal, 0) / salesBreakdownData.length).toFixed(2) : '0.00'}
                  </Text>
                </Box>
                <Box p={3} borderWidth="1px" borderRadius="md" minW="150px">
                  <Text fontSize="sm" color="gray.600">Total Items Sold</Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                    {salesBreakdownData.reduce((sum, order) => sum + order.itemCount, 0)}
                  </Text>
                </Box>
              </Flex>

              <Divider />

              {/* Detailed Orders Table */}
              <Box>
                <Heading size="md" mb={3}>Order Details</Heading>
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Order Date</Th>
                        <Th>Customer</Th>
                        <Th>Package</Th>
                        <Th>Items</Th>
                        <Th isNumeric>Total</Th>
                        <Th>Details</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {salesBreakdownData.map((order, index) => (
                        <Tr key={order.orderId}>
                          <Td>
                            <Text fontSize="sm">
                              {new Date(order.orderDate).toLocaleDateString()}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(order.orderDate).toLocaleTimeString()}
                            </Text>
                          </Td>
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                {order.userName}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {order.userEmail}
                              </Text>
                            </VStack>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue" size="sm">
                              {order.packageName || 'Custom'}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontSize="sm">{order.itemCount} items</Text>
                          </Td>
                          <Td isNumeric>
                            <Text fontWeight="bold" color="green.600">
                              ${order.orderTotal.toFixed(2)}
                            </Text>
                          </Td>
                          <Td>
                            {order.items.length > 0 ? (
                              <VStack align="start" spacing={1}>
                                {order.items.slice(0, 2).map((item, idx) => (
                                  <Text key={idx} fontSize="xs" color="gray.600">
                                    {item.name} - ${parseFloat(item.price).toFixed(2)}
                                  </Text>
                                ))}
                                {order.items.length > 2 && (
                                  <Text fontSize="xs" color="blue.500">
                                    +{order.items.length - 2} more...
                                  </Text>
                                )}
                              </VStack>
                            ) : (
                              <Text fontSize="xs" color="gray.400">No item details</Text>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SalesBreakdownModal;